import { Injectable, inject } from '@angular/core';
import { map, Observable, withLatestFrom } from 'rxjs';
import { CountryCode, FormName, ProjectType } from '@nx-customer-apps/shared/enums';
import { HeatloadStore } from '../../state/heatload/heatload.store';
import { Question } from '@nx-customer-apps/shared/utils';
import { ProjectStore } from '../../state/project/project.store';
import { HeatloadPrefillMapper, ProjectsService } from '../../services';
import { EnergyStandard } from '@nx-customer-apps/api-planning-projects';
import { TranslateService } from '@ngx-translate/core';
import { HeatloadFormOptionsService } from './heatload-form-options.service';
import { RollOutPolicy } from '../../policies';
import { AccordionService } from '../../shared/interfaces';
import { EnergyStandardFormService } from '../../shared/components/energy-standard';

@Injectable({
    providedIn: 'root'
})
export class EnergyStandardService implements AccordionService {
    private prefillMapper = inject(HeatloadPrefillMapper);
    private formService = inject(EnergyStandardFormService);
    private heatloadStore = inject(HeatloadStore);
    private heatloadFormOptionsService = inject(HeatloadFormOptionsService);
    private projectStore = inject(ProjectStore);
    private projectsService = inject(ProjectsService);
    private rollOutPolicy = inject(RollOutPolicy);
    private translate = inject(TranslateService);

    public questions$: Observable<Question[] | null> = this.heatloadStore.energyStandards$.pipe(
        withLatestFrom(this.projectStore.project$, this.heatloadStore.cache$),
        map(([energyStandards, project, cache]) => ({
            energyStandards,
            project,
            cache
        })),
        map(state => {
            if (!state.energyStandards) {
                return null;
            }
            const cachePrefill = this.prefillMapper.stateToEnergyStandardPrefill(state.cache?.selectedEnergyStandard);
            const projectPrefill = this.prefillMapper.projectToEnergyStandardPrefill(state.project, state.energyStandards);
            const formOptions = this.heatloadFormOptionsService.getEnergyStandardFormOptions({
                prefill: cachePrefill || projectPrefill,
                energyStandards: state.energyStandards
            });
            return this.formService.getEnergyStandardQuestions(formOptions);
        })
    );

    public getSuccessMessage(value: EnergyStandard): string {
        return this.translate.instant(`COMMON.ENERGY_STANDARDS.${value.countryCode}.${value.key}`);
    }

    public isExpanded$ = this.isSameValueSubmitedLivingArea$();

    public title$: Observable<string | undefined> = this.projectStore.project$.pipe(
        map(project => {
            const countryCode = project?.address.countryCode as CountryCode;
            return this.rollOutPolicy.getEnergyStandardTitle(countryCode);
        })
    );

    public description$: Observable<string | undefined> = this.projectStore.project$.pipe(
        map(project => {
            const countryCode = project?.address.countryCode as CountryCode;
            return this.rollOutPolicy.getEnergyStandardDescrition(countryCode);
        })
    );

    private isSameValueSubmitedLivingArea$(): Observable<boolean> {
        return this.heatloadStore.sameValueSubmited$.pipe(
            withLatestFrom(this.projectStore.project$, (form, project) => ({ form, project })),
            map(
                ({ form, project }) =>
                    form?.formName === FormName.LivingArea && this.projectsService.isNewConstruction(project!.projectType as ProjectType)
            )
        );
    }
}
